import TrendingUpIcon from '@material-ui/icons/TrendingUp'
import EmojiPeopleIcon from '@material-ui/icons/EmojiPeople'
import ChildCareIcon from '@material-ui/icons/ChildCare'
import GolfCourseIcon from '@material-ui/icons/GolfCourse'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import StarsIcon from '@material-ui/icons/Stars'

export const PGA_COACH_LAST_SEARCH_STORAGE_KEY = 'pga-coach-journey-last-search'
export const PGA_COACH_SAVED_SEARCHES_STORAGE_KEY = 'pga-coach-journey-saved-searches'
export const PGA_COACH_SAVED_OFFERINGS_KEY = 'pga-coach-saved-offerings'
export const EMPTY_RESULTS = {
  hits: [],
  allHits: [],
  unfilteredHits: [],
  totalHits: 0,
  totalPages: 1,
  page: 1,
  perPage: 12,
  hasMore: false,
  nextPage: null,
  /** True when Algolia has bookable offerings in the current geo window without persona refinement. */
  hasOfferingsInAreaIgnoringPersona: false,
}

/** Offering-first program search route (legacy coach directory stays on `/coaches/search`). */
export const PGA_COACH_PROGRAMS_SEARCH_PATHNAME = '/golf-lessons'
/** Previous offering-first search prefix; 302s to `PGA_COACH_PROGRAMS_SEARCH_PATHNAME`. */
export const PGA_COACH_PROGRAMS_LEGACY_SEARCH_PATHNAME = '/coaches/programs'

export const PGA_COACH_PERSONAS = [
  {
    id: 'start-golf',
    algoliaId: 'new_to_golf',
    label: 'Beginner',
    /** Editorial tags / series in Contentful may still reference the prior tab name. */
    guideTagLabels: ['Start Golf'],
    heroLabel: "I'm a beginner golfer",
    resultsLabel: 'Beginner coaching',
    resultsHeadlineTemplate: 'Adults new to golf coaching near {{location}}',
    icon: EmojiPeopleIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Tell us a little about yourself and we will guide you to the right experience.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Welcoming first steps, intro lessons, and low-pressure programs.',
    offeringCards: [
      {
        title: '1:1 Intro Lesson',
        description: 'Build confidence with a beginner-friendly first lesson.',
        format: 'private',
        tags: ['intro-lesson', 'beginner'],
      },
      {
        title: 'Private Lesson',
        description: 'Get tailored instruction that meets you where you are.',
        format: 'private',
        tags: ['private', 'beginner'],
      },
      {
        title: 'Beginner Clinics',
        description: 'Learn the fundamentals in a welcoming group setting.',
        format: 'group',
        tags: ['clinic', 'beginner'],
      },
    ],
  },
  {
    id: 'play-better',
    algoliaId: 'improve_my_game',
    label: 'Improving',
    guideTagLabels: ['Play Better', 'Improving'],
    heroLabel: "I'm a developing player",
    resultsLabel: 'Game improvement',
    resultsHeadlineTemplate: 'Game improvement coaching near {{location}}',
    icon: TrendingUpIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Explore outcome-driven coaching built around consistency, confidence, and progress.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Outcome-driven coaching for better ball striking, distance, and consistency.',
    offeringCards: [
      {
        title: 'Private Lesson',
        description: 'Work one-on-one on the parts of your game that need the most attention.',
        format: 'private',
        tags: ['private', 'improvement'],
      },
      {
        title: 'On-Course Lesson',
        description: 'Take your practice to the course with real-time coaching and strategy.',
        format: 'private',
        tags: ['on-course', 'improvement'],
      },
      {
        title: 'Swing Evaluation',
        description: 'Get a coach-led swing assessment and a plan for better ball striking.',
        format: 'private',
        tags: ['swing-evaluation', 'improvement'],
      },
      {
        title: 'Club Fitting',
        description: 'Dial in equipment that supports your swing and performance goals.',
        format: 'private',
        tags: ['club-fitting', 'improvement'],
      },
    ],
  },
  {
    id: 'break-100',
    algoliaId: 'lower_my_scores',
    label: 'Advanced',
    guideTagLabels: ['Break 100+'],
    heroLabel: 'I want to lower my scores',
    resultsLabel: 'Score-focused coaching',
    resultsHeadlineTemplate: 'Score-focused coaching near {{location}}',
    icon: GolfCourseIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Target the skills and decisions that help scores drop faster.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Scoring-focused coaching, short game help, and on-course strategy.',
    offeringCards: [
      {
        title: 'Private Lesson',
        description: 'Focus one-on-one on the scoring skills that will help you break 100.',
        format: 'private',
        tags: ['private', 'scoring'],
      },
      {
        title: 'Swing Evaluation',
        description: 'Identify the biggest swing changes that can lower your scores faster.',
        format: 'private',
        tags: ['swing-evaluation', 'scoring'],
      },
      {
        title: 'On-Course Lesson',
        description: 'Work on club selection, decisions, and execution where scores are made.',
        format: 'private',
        tags: ['on-course', 'scoring'],
      },
      {
        title: 'Club Fitting',
        description: 'Make sure your equipment supports more consistent scoring.',
        format: 'private',
        tags: ['club-fitting', 'scoring'],
      },
    ],
  },
  {
    id: 'juniors',
    algoliaId: 'junior',
    label: 'Juniors',
    heroLabel: 'Programs for my junior golfer',
    resultsLabel: 'Junior programs',
    resultsHeadlineTemplate: 'Junior golf programs near {{location}}',
    icon: ChildCareIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Structured programs with clear schedules, trusted coaches, and room to grow.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Structured junior clinics, camps, and progression-based programs.',
    offeringCards: [
      {
        title: 'Private Lesson',
        description: 'Get one-on-one instruction tailored to your junior golfer.',
        format: 'private',
        tags: ['private', 'junior'],
      },
      {
        title: 'PGA Jr. League',
        description: 'Join team-based junior golf built around fun, learning, and competition.',
        format: 'program',
        tags: ['jr-league', 'junior'],
      },
      {
        title: 'Family Golf',
        description: 'Learn and play together in family-friendly golf experiences.',
        format: 'group',
        tags: ['family-golf', 'junior'],
      },
      {
        title: 'Group Events',
        description: 'Explore junior group events designed to build skills and confidence.',
        format: 'group',
        tags: ['group-events', 'junior'],
      },
    ],
  },
  {
    id: 'womens-golf',
    algoliaId: 'womens_golf',
    label: 'Women',
    guideTagLabels: ["Women's Golf"],
    heroLabel: 'Programs for women',
    resultsLabel: "Women's golf",
    resultsHeadlineTemplate: "Women's golf coaching near {{location}}",
    icon: FavoriteBorderIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Find comfortable, confidence-building coaching and social learning environments.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Community-first clinics, leagues, and beginner-friendly group coaching.',
    offeringCards: [
      {
        title: 'Private Lesson',
        description: 'Get personalized coaching built around your goals and comfort level.',
        format: 'private',
        tags: ['private', 'women'],
      },
      {
        title: "Women's Group Clinic",
        description: 'Learn in a welcoming group setting with other women golfers.',
        format: 'group',
        tags: ['group-clinic', 'women'],
      },
      {
        title: 'On-Course Lesson',
        description: 'Build confidence on the course with real-time coaching and support.',
        format: 'private',
        tags: ['on-course', 'women'],
      },
    ],
  },
  {
    id: 'military-veterans',
    algoliaId: 'military_veterans',
    label: 'Military & Veterans',
    heroLabel: 'Programs for military & veterans',
    resultsLabel: 'Military & veterans programs',
    resultsHeadlineTemplate: 'Military and veterans golf programs near {{location}}',
    icon: StarsIcon,
    headlineTemplate: 'Golf instruction in {{location}}.',
    summary: 'Discover structured programs built around camaraderie, routine, and support.',
    ctaLabel: 'See Programs Near You',
    ctaLabelMobile: 'Find Programs',
    searchDescription: 'Veteran-focused golf programming, community clinics, and PGA HOPE-style support.',
    offeringCards: [
      {
        title: '1:1 Intro Lesson',
        description: 'Start with an individual lesson built around comfort, support, and progress.',
        format: 'private',
        tags: ['intro-lesson', 'veterans'],
      },
      {
        title: 'Private Lesson',
        description: 'Get one-on-one coaching tailored to your goals and pace.',
        format: 'private',
        tags: ['private', 'veterans'],
      },
      {
        title: 'PGA HOPE',
        description: 'Explore veteran-focused golf programming centered on camaraderie and support.',
        format: 'group',
        tags: ['pga-hope', 'veterans'],
      },
      {
        title: 'Group Events',
        description: 'Join group golf events that build community and confidence.',
        format: 'group',
        tags: ['group-events', 'veterans'],
      },
    ],
  },
]

export const PGA_COACH_PERSONA_MAP = PGA_COACH_PERSONAS.reduce((acc, persona) => {
  acc[persona.id] = persona
  return acc
}, {})

/**
 * Group-events Algolia `tags` mixes coach-selected facets (Kid Friendly, On course, …)
 * with **format/category** facets (Clinic, Camp, …). Offering cards surface audience/experience picks,
 * not these format placeholders — see {@link buildJourneyOfferingCardTagLabels}.
 */
export const PGA_COACH_GROUP_EVENT_OFFERING_FORMAT_FACET_TAGS = new Set([
  'camp',
  'clinic',
  'group-lesson',
  'instructional',
  'program',
  'workshop',
])

export const PGA_COACH_ALLOWED_PERSONA_IDS = PGA_COACH_PERSONAS.map((persona) => persona.id)

/** Maps Algolia persona values → canonical app persona IDs (derived from algoliaId fields above). */
export const PGA_COACH_PERSONA_ALIAS_MAP = PGA_COACH_PERSONAS.reduce((acc, persona) => {
  if (persona.algoliaId) acc[persona.algoliaId] = persona.id
  return acc
}, {})

/** Returns the Algolia persona value for a given app persona ID, falling back to the ID itself. */
export const getAlgoliaPersonaId = (appPersonaId) => PGA_COACH_PERSONA_MAP[appPersonaId]?.algoliaId || appPersonaId

/** Path segment after /coaches/{city}/... maps to persona (slug === persona id). */
export const PGA_COACH_PATH_INTENT_SLUGS = PGA_COACH_PERSONAS.reduce((acc, persona) => {
  acc[persona.id] = { persona: persona.id }
  return acc
}, {})

export const PGA_COACH_FORMAT_OPTIONS = [
  { id: 'private', label: 'Private' },
  { id: 'group', label: 'Group' },
  { id: 'program', label: 'Program' },
]

/**
 * Landscape stock photo used on private offering list cards, detail dialogs,
 * and map pin popup thumbs when the hit has no featured image. Keep this in
 * sync so the popup row matches the listing card, not a coach avatar.
 */
export const PGA_COACH_PRIVATE_LISTING_IMAGE = 'https://images.ctfassets.net/56u5qdsjym8c/5ZVW3cvsJwMVABq3ztJ0JM/0608b645fa5b01c862fc6308a6ec190d/GG1.jpg?q=80'

export const PGA_COACH_PRIVATE_CATEGORY_OPTIONS = [
  { id: 'intro', label: 'Intro' },
  { id: 'private_instruction', label: 'Private' },
  { id: 'on_course', label: 'On course' },
  { id: 'swing_evaluation', label: 'Swing evaluation' },
  { id: 'club_fitting', label: 'Club fitting' },
  { id: 'other', label: 'Other' },
]

export const PGA_COACH_PRICE_OPTIONS = [
  {
    id: 'all',
    label: 'Any',
    min: null,
    max: null,
  },
  {
    id: 'free',
    label: 'Free',
    min: 0,
    max: 0,
  },
  {
    id: 'under-50',
    label: 'Under $50',
    min: null,
    max: 50,
  },
  {
    id: 'under-100',
    label: 'Under $100',
    min: null,
    max: 100,
  },
  {
    id: '100-plus',
    label: '$100+',
    min: 100,
    max: null,
  },
]

/** Distance radius for map + Algolia aroundRadius (mi). */
export const PGA_COACH_DISTANCE_OPTIONS = [
  { id: '5', label: '5 mi', miles: 5 },
  { id: '10', label: '10 mi', miles: 10 },
  { id: '25', label: '25 mi', miles: 25 },
  { id: '50', label: '50 mi', miles: 50 },
]

/** Time window filter; maps to availability_windows (private index) and start_date_time_ts (group events index). */
export const PGA_COACH_SCHEDULE_OPTIONS = [
  { id: 'any', label: 'Any time' },
  { id: 'this_week', label: 'This week' },
  { id: 'next_week', label: 'Next week' },
  { id: 'this_month', label: 'This month' },
  { id: 'next_month', label: 'Next month' },
]

export const PGA_COACH_SORT_OPTIONS = [
  { id: 'recommended', label: 'Best match' },
  { id: 'price-asc', label: 'Price ↑' },
  { id: 'price-desc', label: 'Price ↓' },
]

export const PGA_COACH_TRUST_PILLARS = [
  {
    id: 'certified-experts',
    title: 'Certified Experts',
    description: 'Trusted golf coaches with the training and experiences to help you improve.',
    icon: StarsIcon,
  },
  {
    id: 'tailored-to-you',
    title: 'Tailored to You',
    description: 'Coaching adapted to your goals, skill level, and pace.',
    icon: StarsIcon,
  },
  {
    id: 'faster-improvement',
    title: 'Faster Improvement',
    description: 'Expert guidance for real, measurable progress.',
    icon: StarsIcon,
  },
  {
    id: 'something-for-everyone',
    title: 'Something for Everyone',
    description: 'Lessons, clinics, content, and family programs.',
    icon: StarsIcon,
  },
]

export const PGA_COACH_JOURNEY_ANALYTICS = {
  heroSearch: 'pga-coach-journey-search',
  heroPersonaSelect: 'pga-coach-journey-persona-select',
  personaTabClick: 'pga-coach-journey-persona-tab-click',
  viewResults: 'view-pga-coach-offering-results',
  offeringCardImpression: 'view-pga-coach-offering-card',
  externallyBookableCoachCardImpression: 'view-pga-coach-externally-bookable-coach-card',
  externallyBookableCoachCardClick: 'pga-coach-journey-externally-bookable-coach-click',
  offeringClick: 'pga-coach-journey-offering-click',
  offeringActionButtonClick: 'pga-coach-journey-offering-action-button-click',
  offeringDialogActionClick: 'pga-coach-journey-offering-dialog-action-click',
  offeringFavoriteToggle: 'pga-coach-journey-offering-favorite-toggle',
  savedOfferingsFilterToggle: 'pga-coach-journey-saved-offerings-filter-toggle',
  saveSearch: 'pga-coach-journey-save-search',
  locationChange: 'pga-coach-journey-location-change',
  filterChange: 'pga-coach-journey-filter-change',
  clickMapPin: 'pga-coach-journey-click-map-pin',
  toggleMapView: 'pga-coach-journey-toggle-map-view',
  coachInfoPanelOpen: 'pga-coach-journey-coach-info-panel-open',
  coachInfoPanelCtaClick: 'pga-coach-journey-coach-info-panel-cta-click',
}

export const PGA_COACH_CITY_LINKS = [
  { label: 'Frisco', slug: 'frisco', stateSlug: 'tx' },
  { label: 'Atlanta', slug: 'atlanta', stateSlug: 'ga' },
  { label: 'Boston', slug: 'boston', stateSlug: 'ma' },
  { label: 'Dallas', slug: 'dallas', stateSlug: 'tx' },
  { label: 'Denver', slug: 'denver', stateSlug: 'co' },
  { label: 'Houston', slug: 'houston', stateSlug: 'tx' },
  { label: 'Miami', slug: 'miami', stateSlug: 'fl' },
  { label: 'Phoenix', slug: 'phoenix', stateSlug: 'az' },
  { label: 'San Francisco', slug: 'san-francisco', stateSlug: 'ca' },
  { label: 'Seattle', slug: 'seattle', stateSlug: 'wa' },
  { label: 'Washington DC', slug: 'washington', stateSlug: 'dc' },
]

export const PGA_COACH_LANDING_GUIDES_SECTION_ID = 'guides'

export const PGA_COACH_DEFAULT_CONTENT = {
  headline: 'Getting better at golf, made easy.',
  subheadline: 'Expert instruction for every golfer. Discover coaches, programs, and lessons tailored to your game.',
  personaSectionSummary: "Tell us a little about yourself and we'll guide you to the right experience.",
  coachRailHeadline: 'Coaches who can help you get there',
  coachRailSummary: 'PGA Coaches offering programs tailored to your goals',
  trustHeadline: 'What Sets Us Apart',
  trustSummary: 'Learn the basics, improve your skills, and enjoy the game with our expert coaches.',
  guidesHeadline: 'Helpful guides to get you playing',
  guidesSummary: 'Helpful content for golfers who want a little more confidence before they book.',
  guidesCta: 'See Helpful Guides',
  citySectionHeadline: 'Golf coaching, coast to coast.',
  citySectionSummary: 'Find expert golf coaching in your city - PGA Coaches are everywhere.',
  citySectionCta: 'Find Coaching in Your City',
  viewAllCoachesLabel: 'Browse Coaches',
}
